// Where the Dock sits on the screen.
import { useLayoutEffect, useState } from "react";
import { useViewportWidth } from "../shell/layout";

/**
 * The Dock's distance from the screen's bottom: above the home bar (its
 * height) and 5 px more, or 17 px where there is none. The Agent chat's post
 * box sat at this height and every Dock was aligned to it (2026-10-01). Kept
 * as a formula, not a measured number: Chrome's bottom bar folding changes the
 * home bar's inset (0 → about 34 px), and a written number left the Dock
 * where it was (2026-09-28). The home bar's room is styles' `--dock-floor`.
 */
export const DOCK_BOTTOM = "calc(var(--dock-floor) + 5px)";

/**
 * Room a scrolling list keeps at its end so its last row comes out above the
 * Dock (its place + its height 48 + 12 between): without it the last row
 * stayed behind the Dock (2026-10-01).
 */
export const DOCK_CLEARANCE = `calc(${DOCK_BOTTOM} + 60px)`;

/**
 * The frame's width: the window's less the side insets it keeps clear
 * (phone.css). On the iPhone Duo's cover screen the home screen app runs
 * under the system's column at the right (84 pt); a Dock fitted to the
 * window's width ran into it.
 */
export function useFrameWidth(): number {
  const viewport = useViewportWidth();
  const [width, setWidth] = useState<number | null>(null);
  // frontend-development: not a fetch; follows the frame's measured width.
  useLayoutEffect(() => {
    const frame = document.querySelector<HTMLElement>("[data-me-frame]");
    if (!frame || typeof ResizeObserver === "undefined") return;
    // 0 is a frame not laid out (yet): keep to the viewport then.
    const read = () => setWidth(frame.clientWidth || null);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(frame);
    return () => ro.disconnect();
  }, []);
  return width ?? viewport;
}
